<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="items"
      :search="search"
      class="elevation-1"
    >
      <!-- Attachment thumbnails -->
      <template
        v-for="header in attachmentHeaders"
        v-slot:[`item.${header.value}`]="{ item }"
      >
        <v-img
          :key="header.value"
          :src="item[header.value]"
          max-width="50"
          max-height="50"
          contain
          style="cursor: pointer"
          @click="openImage(header, item)"
        ></v-img>
      </template>
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>{{ tableDisplayName }} </v-toolbar-title>
          <v-divider class="mx-4" inset vertical></v-divider>
          <v-dialog v-model="dialog" max-width="500px">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                class="text-capitalize"
                color="#B7CDAD"
                v-bind="attrs"
                v-on="on"
              >
                New Entry
              </v-btn>
            </template>
            <v-card>
              <v-card-title>
                <span class="text-h5">{{ formTitle }}</span>
              </v-card-title>

              <v-card-text>
                <v-container>
                  <v-row>
                    <v-col
                      v-for="(column, index) in headers.slice(
                        0,
                        headers.length - 1,
                      )"
                      :key="index"
                      cols="12"
                      sm="6"
                      md="4"
                    >
                      <v-text-field
                        v-if="column.type === 'number'"
                        class="text-body-2 white"
                        :disabled="!column.editable || disableAllInputs"
                        v-model.number="editedItem[column.value]"
                        :type="column.type"
                        :label="column.text"
                        color="black"
                        hide-details
                        height="28"
                        flat
                        outlined
                        dense
                        min="0"
                      >
                      </v-text-field>
                      <!-- selection type -->
                      <v-autocomplete
                        v-else-if="column.type === 'select'"
                        class="text-body-2 white"
                        :disabled="!column.editable || disableAllInputs"
                        :label="column.text"
                        v-model="editedItem[column.value]"
                        :items="column.options"
                        color="black"
                        hide-details
                        height="28"
                        flat
                        outlined
                        dense
                        :rules="[
                          (v) =>
                            !!v ||
                            !column.text.includes('*') ||
                            'Selection required',
                        ]"
                        required
                      >
                      </v-autocomplete>
                      <!-- date type -->
                      <v-text-field
                        v-else-if="column.type === 'date'"
                        class="text-body-2 white"
                        :disabled="!column.editable || disableAllInputs"
                        v-model="editedItem[column.value]"
                        :label="column.text"
                        :type="column.type"
                        :min="tableDataset?.DateRangeStart || undefined"
                        :max="tableDataset?.DateRangeEnd || undefined"
                        color="black"
                        hide-details
                        height="28"
                        flat
                        outlined
                        dense
                      >
                      </v-text-field>

                      <v-text-field
                        v-else
                        class="text-body-2 white"
                        :disabled="
                          !column.editable ||
                          disableAllInputs ||
                          column.type === 'attachment'
                        "
                        v-model="editedItem[column.value]"
                        :label="column.text"
                        :type="column.type"
                        color="black"
                        hide-details
                        height="28"
                        flat
                        outlined
                        dense
                      >
                      </v-text-field>
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>

              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="warning" text @click="close"> Cancel </v-btn>
                <v-btn
                  color="black"
                  text
                  @click="saveItem"
                  :loading="dialogSaving"
                >
                  <v-icon v-if="dialogSaveSuccess" color="success"
                    >mdi-check-circle</v-icon
                  >
                  <v-icon v-else-if="dialogSaveError" color="error"
                    >mdi-alert-circle</v-icon
                  >
                  Save
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
          ></v-text-field>
        </v-toolbar>
        <p class="mx-3 px-1 text-caption">
          {{ tableDataset.Description }}
        </p>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-icon
          v-for="(action, index) in item.actions"
          class="mr-2"
          medium
          :color="action.color"
          :key="index"
          @click="action.handler(item)"
        >
          {{ action.icon }}
        </v-icon>
      </template>
      <template v-slot:no-data>
        <v-subheader class="justify-center">No entries found</v-subheader>
      </template>
    </v-data-table>
    <!-- Image preview dialog -->
    <v-dialog v-model="imageDialog" max-width="600px">
      <v-card>
        <v-img :src="selectedImage" contain></v-img>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { getTable, updateTableRow, deleteTableRow } from "@/api/v2";
import { formatNumber } from "@/helpers/formatting";

export default {
  name: "DataTableAlternate",

  props: {
    tableInformation: {
      type: Object,
      // default: () => ({
      //   Errors: null,
      //   Warnings: null,
      //   DatasetName: "another new ranch/Herd - Size",
      //   DatasetOwner: "cbelisle@iegconsulting.com",
      //   FileId: "dcf04ec8-22c3-4de3-b7dd-a8f727e3059d",
      //   Columns: ["", "Date*", "Herd size (SAU)*", "Herd name"],
      //   Rows: [
      //     [1, "2022-05-15", 22, "Jerseys Heifer 2022"],
      //     [2, "2022-06-08", 35, "Jerseys Heifer 2022"],
      //     [3, "2022-06-22", 31, "Jerseys Heifer 2022"],
      //     [4, "2022-07-03", 30, "Jerseys Heifer 2022"],
      //   ],
      //   ColumnTypes: ["Integer", "StringDate", "Float", "ChoiceString"],
      //   IndexColumn: "",
      //   ImmutableColumns: [""],
      //   ColumnChoices: {
      //     "Herd name": ["Jerseys Heifer 2022"],
      //   },
      //   CustomGetHandlerEndpoint: null,
      //   CustomPutRowHandlerEndpoint: null,
      //   CustomDeleteRowHandlerEndpoint: null,
      // }),
    },
    tableDataset: {
      type: Object,
    },
  },

  data() {
    return {
      // dialog state for image preview
      imageDialog: false,
      selectedImage: null,
      search: "",
      tableTitle: "",
      tableDataResponse: null,
      dialog: false,
      dialogDelete: false,
      rowDeleting: false,
      dialogSaving: false,
      dialogSaveSuccess: false,
      dialogSaveError: false,
      disableAllInputs: false,
      headers: [],
      items: [],
      actions: [
        {
          text: "Edit",
          icon: "mdi-pencil",
          description: "Edit Entry",
          color: "black",
          handler: (item) => {
            this.editItem(item);
          },
        },
        {
          text: "Delete",
          icon: "mdi-delete",
          description: "Delete Entry",
          color: "red darken-2",
          handler: (item) => {
            this.deleteItem(item);
          },
        },
      ],
      editedIndex: -1,
      editedItem: {},
      defaultItem: {},
      nonEditableTables: ["Vegetation Data"],
    };
  },

  computed: {
    formTitle() {
      return this.editedIndex === -1 ? "New Entry" : "Edit Entry";
    },
    tableDisplayName() {
      return this.tableInformation
        ? this.tableInformation.DatasetName.split("/")[1]
        : null;
    },
    indexColumn() {
      const indexColumn = (column) => {
        if (column === "") return "rowIndex";
        else return column;
      };
      return indexColumn(this.tableInformation.IndexColumn);
    },
    attachmentHeaders() {
      return this.headers.filter((h) => h.type === "attachment");
    },
  },

  watch: {
    dialog(val) {
      if (val && this.editedIndex === -1) {
        this.editedItem["0"] = this.items.length + 1;
      }
      val || this.close();
    },

    dialogDelete(val) {
      val || this.closeDelete();
    },
  },

  created() {
    this.initialize(this.tableInformation);
  },

  methods: {
    initialize(tableInformation) {
      this.fetchTableData(tableInformation);
    },

    async fetchTableData(tableInformation) {
      try {
        const response = await getTable(
          tableInformation.DatasetName,
          tableInformation.DatasetOwner,
          tableInformation.FileId,
          false,
        );

        this.tableDataResponse = response;

        this.headers = this.buildPropertyHeaders(tableInformation);

        this.items = response.Rows.map((row) => {
          let item = {};
          row.forEach((cell, index) => {
            item[`${index}`] = cell;
          });
          item.actions = this.actions;

          return item;
        });

        this.defaultItem = {};
        this.headers.forEach((header) => {
          this.defaultItem[header.value] = "";
        });
      } catch (error) {
        console.error(error);
      }
    },

    buildPropertyHeaders(tableInformation) {
      const headers = tableInformation.Columns;
      const headerTypes = tableInformation.ColumnTypes;
      const immutableColumns = tableInformation.ImmutableColumns
        ? tableInformation.ImmutableColumns
        : [];

      const headerType = (type, column) => {
        switch (type) {
          case "String":
            return column.includes("Time") ? "time" : "text";

          case "StringDate":
            return "date";

          case "ChoiceString":
            return "select";

          case "Integer":
          case "Float":
            return "number";
          case "Attachment":
            return "attachment";

          default:
            return "text";
        }
      };

      const builtHeaders = headers.map((column, index) => ({
        value: `${index}`,
        text: column,
        options: (tableInformation.ColumnChoices[column] ?? []).sort((a, b) => {
          const aNumber = parseInt(a.split(" ")[0]);
          const bNumber = parseInt(b.split(" ")[0]);
          if (!isNaN(aNumber) && !isNaN(bNumber)) {
            return aNumber - bNumber;
          } else {
            return a.localeCompare(b);
          }
        }),
        sortable: false,
        editable: !immutableColumns.includes(column),
        type: headerType(headerTypes[index], column),
        formatter: (x) => (typeof x === "number" ? formatNumber(x) : x),
      }));

      if (this.actions.length > 0)
        builtHeaders.push({
          text: `${
            this.actions.length === 1 ? this.actions[0].text : "Actions"
          }`,
          value: "actions",
          sortable: false,
        });

      return builtHeaders;
    },

    editItem(item) {
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },

    async saveItem() {
      if (this.disableAllInputs) return;

      this.disableAllInputs = true;
      this.dialogSaving = true;
      function getValue(column, index, editedRow, isOrderedTable) {
        let value = editedRow[index];

        if (column === "" && isOrderedTable) {
          return editedRow["0"];
        }

        if (column.includes("Time")) {
          if (value && value.length === 5) {
            value += ":00"; // Append seconds if only HH:MM is provided
          }
        }

        if (index > 1 && value === "") {
          return null;
        }

        return value;
      }

      const validateDateRange = (
        column,
        value,
        dateRangeStart,
        dateRangeEnd,
      ) => {
        if (!column.includes("Date") || !value) {
          return; // Only validate fields with "Date" in the name that have values
        }

        if (!dateRangeStart && !dateRangeEnd) {
          return; // No range specified, skip validation
        }

        const dateValue = new Date(value);

        if (isNaN(dateValue.getTime())) {
          throw new Error(`The ${column} field contains an invalid date.`);
        }

        if (dateRangeStart) {
          const minDate = new Date(dateRangeStart);
          if (dateValue < minDate) {
            throw new Error(
              `The ${column} field must be on or after ${dateRangeStart}.`,
            );
          }
        }

        if (dateRangeEnd) {
          const maxDate = new Date(dateRangeEnd);
          if (dateValue > maxDate) {
            throw new Error(
              `The ${column} field must be on or before ${dateRangeEnd}.`,
            );
          }
        }
      };

      const validateNumberRange = (column, value, columnType) => {
        // Only validate number types (Integer or Float)
        if (
          !columnType ||
          (columnType !== "Integer" && columnType !== "Float")
        ) {
          return;
        }

        // Skip validation for null/undefined values
        if (value === null || value === undefined || value === "") {
          return;
        }

        const numValue = Number(value);

        if (isNaN(numValue)) {
          throw new Error(`The ${column} field must be a valid number.`);
        }

        if (numValue < 0) {
          throw new Error(
            `The ${column} field must be zero or a positive number. Negative values are not allowed.`,
          );
        }
      };

      const validateTimeFormat = (column, value) => {
        // Only validate fields with "Time" in the name
        if (!column.includes("Time") || !value) {
          return;
        }

        // Check for HH:MM:SS format (value should already be padded by getValue)
        const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9])$/;

        if (!timeRegex.test(value)) {
          throw new Error(
            `The ${column} field must be in HH:MM:SS format (e.g., 09:30:00 or 14:45:30).`,
          );
        }
      };

      const validateRequiredFields = (column, value) => {
        if (column.includes("*") && (value === null || value === "")) {
          throw new Error(
            `The ${column} field is required and cannot be empty.`,
          );
        }
      };

      try {
        const isOrderedTable = this.indexColumn === "rowIndex" ? true : false;

        const unformattedRowData = this.tableInformation.Columns.map(
          (column, index) => {
            const value = getValue(
              column,
              index,
              this.editedItem,
              isOrderedTable,
            );

            // Validate required fields
            validateRequiredFields(column, value);

            // Validate date fields against range if specified
            validateDateRange(
              column,
              value,
              this.tableDataset?.DateRangeStart,
              this.tableDataset?.DateRangeEnd,
            );

            // Validate number fields to ensure only zero or positive values
            validateNumberRange(
              column,
              value,
              this.tableInformation.ColumnTypes[index],
            );

            // Validate time fields to ensure HH:MM:SS format
            validateTimeFormat(column, value);

            return value;
          },
        );

        await updateTableRow(this.tableInformation, unformattedRowData);

        this.initialize(this.tableInformation);

        this.dialogSaveSuccess = true;
      } catch (error) {
        this.dialogSaveError = true;
        this.$showAlert({
          text: error,
          type: "error",
        });
      } finally {
        this.dialogSaving = false;

        if (this.dialogSaveSuccess) {
          setTimeout(() => {
            this.dialogSaveSuccess = false;
            this.close();
          }, 1500);
        }

        if (this.dialogSaveError) {
          setTimeout(() => {
            this.dialogSaveError = false;
          }, 1500);
        }

        this.disableAllInputs = false;
      }
    },
    async deleteItem(item) {
      this.rowDeleting = true;
      this.editedIndex = this.items.indexOf(item);

      let confirmed = await this.$showAlert({
        text: `Are you sure you want to delete the entry for ${
          Object.values(item)[1]
        }?`,
        type: "warning",
        choice: true,
      });

      if (!confirmed) {
        this.rowDeleting = false;
        return;
      }

      try {
        await deleteTableRow(this.tableInformation, this.editedIndex + 1);
        this.initialize(this.tableInformation);

        this.$showAlert({
          text: "Row item successfully deleted.",
          type: "success",
          timeout: 1500,
        });
      } catch (error) {
        this.showAlert({
          text: error,
          type: "error",
        });
      } finally {
        this.rowDeleting = false;
      }
    },
    openImage(header, item) {
      this.selectedImage = item[header.value];
      this.imageDialog = true;
    },
    close() {
      this.dialog = false;
      this.editedItem = Object.assign({}, this.defaultItem);
      this.editedIndex = -1;
    },
    closeDelete() {
      this.dialogDelete = false;
    },
  },
};
</script>
